/**
 * useTrainingRequirements Hook
 * 
 * Manage training requirements and compliance (Catalyst feature).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-L: route training-requirements `completed_at` write through clock.
import { clock } from '@/utils/clock';
import type { 
  TrainingRequirement, 
  TechnicianRequirementStatus,
  RequirementStatus 
} from '@/types/training';

/**
 * Fetch training requirements for a shop
 */
export function useShopRequirements(shopId: string | undefined) {
  return useQuery({
    queryKey: ['training-requirements', shopId],
    queryFn: async () => {
      if (!shopId) return [];
      
      const { data, error } = await supabase
        .from('training_requirements')
        .select(`
          *,
          artifact:training_artifacts(*)
        `)
        .eq('org_id', shopId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return (data || []) as TrainingRequirement[];
    },
    enabled: !!shopId,
  });
}

/**
 * Fetch requirement statuses for a technician
 */
export function useTechnicianRequirements(technicianProfileId: string | undefined) {
  return useQuery({
    queryKey: ['technician-requirements', technicianProfileId],
    queryFn: async () => {
      if (!technicianProfileId) return [];
      
      const { data, error } = await supabase
        .from('technician_requirement_status')
        .select(`
          *,
          requirement:training_requirements(
            *,
            artifact:training_artifacts(*)
          )
        `)
        .eq('technician_profile_id', technicianProfileId)
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      
      return (data || []) as TechnicianRequirementStatus[];
    },
    enabled: !!technicianProfileId,
  });
}

interface CreateRequirementInput {
  orgId: string;
  teamId?: string;
  roleKey?: string;
  domainKey?: string;
  artifactId?: string;
  requirementRule?: Record<string, unknown>;
  requiredByDate?: string;
  createdBy?: string;
  // Batch 18-F46 — admin-configurable manager-verification gate.
  // Defaults to false at the DB level (F41 migration) and at the
  // insert payload below to preserve self-attestation behaviour.
  requiresManagerVerification?: boolean;
}

/**
 * Create a training requirement
 */
export function useCreateRequirement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateRequirementInput) => {
      const { data, error } = await supabase
        .from('training_requirements')
        .insert({
          org_id: input.orgId,
          team_id: input.teamId,
          role_key: input.roleKey,
          domain_key: input.domainKey,
          artifact_id: input.artifactId,
          requirement_rule: input.requirementRule,
          required_by_date: input.requiredByDate,
          created_by: input.createdBy,
          is_active: true,
          requires_manager_verification:
            input.requiresManagerVerification ?? false,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['training-requirements', variables.orgId]
      });
    },
  });
}

/**
 * Update a training requirement
 */
export function useUpdateRequirement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: {
      requirementId: string;
      updates: Partial<CreateRequirementInput> & { isActive?: boolean };
    }) => {
      const { requirementId, updates } = input;
      
      const updateData: Record<string, unknown> = {};
      if (updates.teamId !== undefined) updateData.team_id = updates.teamId;
      if (updates.roleKey !== undefined) updateData.role_key = updates.roleKey;
      if (updates.domainKey !== undefined) updateData.domain_key = updates.domainKey;
      if (updates.artifactId !== undefined) updateData.artifact_id = updates.artifactId;
      if (updates.requirementRule !== undefined) updateData.requirement_rule = updates.requirementRule;
      if (updates.requiredByDate !== undefined) updateData.required_by_date = updates.requiredByDate;
      if (updates.isActive !== undefined) updateData.is_active = updates.isActive;
      // Batch 18-F48 — flag-only edit support. Mapped explicitly
      // so the column is updated only when the caller passes the
      // flag; omitting it preserves the partial-update contract.
      if (updates.requiresManagerVerification !== undefined) {
        updateData.requires_manager_verification = updates.requiresManagerVerification;
      }
      
      const { data, error } = await supabase
        .from('training_requirements')
        .update(updateData)
        .eq('id', requirementId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-requirements'] });
    },
  });
}

interface UpdateRequirementStatusInput {
  requirementId: string;
  technicianProfileId: string;
  status: RequirementStatus;
  waivedReason?: string;
  waivedBy?: string;
}

/**
 * Update or create a technician's requirement status
 */
export function useUpdateRequirementStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: UpdateRequirementStatusInput) => {
      const { requirementId, technicianProfileId, status, waivedReason, waivedBy } = input;
      
      const upsertData: Record<string, unknown> = {
        requirement_id: requirementId,
        technician_profile_id: technicianProfileId,
        status,
      };
      
      if (status === 'completed') {
        upsertData.completed_at = clock.now().toISOString();
      }
      
      if (status === 'waived') {
        upsertData.waived_reason = waivedReason;
        upsertData.waived_by = waivedBy;
      }
      
      const { data, error } = await supabase
        .from('technician_requirement_status')
        .upsert(upsertData as any, {
          onConflict: 'requirement_id,technician_profile_id',
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['technician-requirements', variables.technicianProfileId] 
      });
    },
  });
}

/**
 * Get compliance summary for a technician
 */
export function useComplianceSummary(technicianProfileId: string | undefined) {
  const { data: requirements } = useTechnicianRequirements(technicianProfileId);
  
  return useQuery({
    queryKey: ['compliance-summary', technicianProfileId],
    queryFn: () => {
      if (!requirements || requirements.length === 0) {
        return {
          total: 0,
          completed: 0,
          inProgress: 0,
          overdue: 0,
          assigned: 0,
          waived: 0,
          complianceRate: 100,
        };
      }
      
      const completed = requirements.filter(r => r.status === 'completed').length;
      const inProgress = requirements.filter(r => r.status === 'in_progress').length;
      const overdue = requirements.filter(r => r.status === 'overdue').length;
      const assigned = requirements.filter(r => r.status === 'assigned').length;
      const waived = requirements.filter(r => r.status === 'waived').length;
      const total = requirements.length;
      
      // Compliance = (completed + waived) / (total - waived) * 100
      const relevantTotal = total - waived;
      const complianceRate = relevantTotal > 0 
        ? (completed / relevantTotal) * 100 
        : 100;
      
      return {
        total,
        completed,
        inProgress,
        overdue,
        assigned,
        waived,
        complianceRate: Math.round(complianceRate),
      };
    },
    enabled: !!technicianProfileId && !!requirements,
  });
}
